import { useEffect, useState } from 'react'
import type { Voice } from '@/api/client'
import { Field, Textarea } from '@/ui'

/** An unset voice, so a caller never has to spell the empty shape. */
export const emptyVoice: Voice = {
  persona: '',
  style_guide: '',
  banned_phrases: [],
  preferred_phrases: [],
}

const lines = (s: string) =>
  s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)

const joined = (v: string[] | null | undefined) => (v ?? []).join('\n')

/** A phrase list typed one per line. The text is kept as typed while the
 *  list above gets the parsed lines: parsing on every key would eat the new
 *  line the moment it is typed. A change from outside (a preset, 되돌리기)
 *  replaces the text. */
function PhraseField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string[] | null
  onChange: (v: string[]) => void
}) {
  const [text, setText] = useState(joined(value))
  useEffect(() => {
    setText((cur) => (joined(lines(cur)) === joined(value) ? cur : joined(value)))
  }, [value])
  return (
    <Field label={label}>
      <Textarea
        rows={4}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          onChange(lines(e.target.value))
        }}
      />
    </Field>
  )
}

/** The four fields that decide how an account sounds. */
export function VoiceFields({ voice, onChange }: { voice: Voice; onChange: (v: Voice) => void }) {
  return (
    <div className="space-y-3">
      <Field label="말투의 주인 — 이 계정이 누구인지">
        <Textarea
          rows={5}
          value={voice.persona}
          onChange={(e) => onChange({ ...voice, persona: e.target.value })}
          placeholder={'1인 개발자. 카메라 앱을 3년째 혼자 만든다.\n실패한 시도와 측정값을 자주 이야기하고, 과장하지 않는다.'}
        />
      </Field>
      <Field label="문체 — 어떻게 쓰는지">
        <Textarea
          rows={4}
          value={voice.style_guide}
          onChange={(e) => onChange({ ...voice, style_guide: e.target.value })}
          placeholder="짧은 문장. 이모지 없음. 숫자는 구체적으로."
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <PhraseField
          label="자주 쓰는 표현 (한 줄에 하나)"
          value={voice.preferred_phrases}
          onChange={(v) => onChange({ ...voice, preferred_phrases: v })}
        />
        <PhraseField
          label="금지 표현 (한 줄에 하나)"
          value={voice.banned_phrases}
          onChange={(v) => onChange({ ...voice, banned_phrases: v })}
        />
      </div>
      <p className="text-meta text-muted-foreground">
        캠페인에도 페르소나가 있으면 그건 "이번 캠페인이 무엇을 다루는지"로 내려가고, 말투는 여기가 이긴다.
        금지·선호 표현은 양쪽 합집합이다.
      </p>
    </div>
  )
}

/** The three prepared voices, one per language account.
 *
 *  They describe the maker rather than any app, which is why the list does not
 *  grow as apps do: a new app becomes a campaign brief, not a new persona. The
 *  differences between them are not stylistic preference — Korean Threads
 *  pushes back on a post that hides price or limits, Japanese Threads answers
 *  to a same-level confessional tone and requires saying an app is your own,
 *  and English build-in-public has been gamed hard enough that only measured
 *  numbers and failures still read as honest.
 *
 *  Kept in step with viral-saas/docs/personas-and-app-briefs.md.
 */
export const PRESETS: Array<{ id: string; label: string; note: string; voice: Voice }> = [
  {
    id: 'ko',
    label: '한국어',
    note: '가격·한계를 먼저. 링크는 물어보면. 맥락 → 막힌 지점 → 좁은 질문',
    voice: {
      persona: [
        '한국어로만 쓴다. 앱을 혼자 만들어 스토어에 올리는 1인 개발자.',
        '30개 넘게 냈고 대부분 잘 안 됐다. 그 과정에서 재본 숫자와 실패한 시도를 이야기한다.',
        '자랑이 아니라 기록으로 쓴다. 모르는 건 모른다고 하고, 가격·제한·안 되는 점을 먼저 말한다.',
        '글 끝에 링크를 붙이지 않는다 — 궁금해하는 사람이 물어보면 그때 준다.',
      ].join('\n'),
      style_guide: [
        '짧게. 3~5문장. 첫 줄에 결론이나 숫자.',
        '"맥락 한 줄 → 내가 막힌 지점 → 좁은 질문" 구조를 자주 쓴다.',
        '해요체. 이모지 없음. 해시태그 없음.',
        '숫자는 반드시 구체적으로(12초 → 0.2초, 코드 111장). 근거 없는 형용사 금지.',
      ].join('\n'),
      preferred_phrases: ['재보니까', '해봤는데 안 됐어요', '이건 아직 못 했어요', '혹시 이런 경우 어떻게 하세요?'],
      banned_phrases: ['혁신적인', '최고의', '게임 체인저', '여러분의 성공', '지금 바로 다운로드', '곧 추가하겠습니다'],
    },
  },
  {
    id: 'ja',
    label: '日本語',
    note: '同じ目線の告白トーン。自分のアプリだと明記（ステマ規制）',
    voice: {
      persona: [
        '日本語だけで書く。アプリを一人で作って出している個人開発者。',
        '30本以上出して、ほとんど伸びなかった。その過程で測った数字と失敗をそのまま置いていく。',
        '上から教えるのではなく、同じ目線で「うまくいかなかった話」を共有する。',
        '自分のアプリの話をするときは必ず「自分が作ったもの」と明記する（ステマにしない）。',
        '売り込まない。値段と制限を先に言う。',
      ].join('\n'),
      style_guide: [
        '短く。2~4文。スクロール中に読める長さ。',
        '弱さから入る:「正直しんどい」「私だけ?」「わかる人いる?」を自然に使う。',
        '敬体だが硬くしない。です・ますで、断定は控える。',
        '数字は具体的に(12秒→0.2秒、コード111枚)。形容詞で盛らない。',
        '絵文字は使わない。ハッシュタグは使わない。',
      ].join('\n'),
      preferred_phrases: ['正直、これはしんどかった', 'わかる人いる?', '気づいたら〇〇してた', '自分で作ってるアプリの話です'],
      banned_phrases: ['革新的', '最高の', '神アプリ', '今すぐダウンロード', '近日追加予定', '絶対おすすめ'],
    },
  },
  {
    id: 'en',
    label: 'English',
    note: 'Measured numbers and failures, not daily progress',
    voice: {
      persona: [
        'Write in English only. Solo developer who ships iOS and Mac apps — thirty-odd',
        'of them, most of which went nowhere. Posts what was measured and what broke,',
        'not daily progress. Has no growth advice to give and says so.',
        'When it is my own app, says so plainly. Names the price and the limits before',
        'anyone has to ask. No link unless someone asks for one.',
      ].join('\n'),
      style_guide: [
        'Three to five sentences. First line carries the number or the conclusion.',
        'Concrete over adjectival: "typing the body took 12 seconds of a 30-second',
        'budget", not "a huge performance win".',
        'No emoji, no hashtags, no threads-of-ten. One idea per post.',
        'A question at the end only when it is a real one.',
      ].join('\n'),
      preferred_phrases: ['measured it and', 'that did not work', 'still have not fixed', 'what do you do about'],
      banned_phrases: ['game changer', 'revolutionary', 'crushing it', 'let that sink in', 'link in bio', 'coming soon'],
    },
  },
]
